import { useCallback } from 'react';

import { create } from 'zustand';

import type {
  RecentsStorageV2,
  SavedGroupsStorageV2,
} from '@/lib/recoil/property-picker-storage-migration';

import {
  type InvoiceItemsViewMode,
  loadFavoriteProperties,
  loadFavoriteTenants,
  loadInvoiceItemsViewMode,
  loadPropertyPickerGroups,
  loadPropertyPickerRecents,
  persistFavoriteProperties,
  persistFavoriteTenants,
  persistInvoiceItemsViewMode,
  persistPropertyPickerGroups,
  persistPropertyPickerRecents,
} from './persist';

export type { InvoiceItemsViewMode };

export type SetStateAction<T> = T | ((prev: T) => T);

function resolveUpdater<T>(prev: T, updater: SetStateAction<T>): T {
  return typeof updater === 'function'
    ? (updater as (prev: T) => T)(prev)
    : updater;
}

export type AppClientState = {
  greeting: string;
  pageBanner: boolean;
  recentPropertySelections: RecentsStorageV2;
  userPropertyGroups: SavedGroupsStorageV2;
  favoriteProperties: string[];
  /** Tenant ids the user starred in the sidebar tenant switcher. */
  favoriteTenantIds: string[];
  invoiceItemsViewMode: InvoiceItemsViewMode;
};

const initialAppClientState = (): AppClientState => ({
  greeting: '',
  pageBanner: false,
  recentPropertySelections: loadPropertyPickerRecents<RecentsStorageV2>({}),
  userPropertyGroups: loadPropertyPickerGroups<SavedGroupsStorageV2>({}),
  favoriteProperties: loadFavoriteProperties(),
  favoriteTenantIds: loadFavoriteTenants(),
  invoiceItemsViewMode: loadInvoiceItemsViewMode(),
});

export const useAppClientStore = create<AppClientState>(() =>
  initialAppClientState()
);

useAppClientStore.subscribe((state, prev) => {
  if (state.recentPropertySelections !== prev.recentPropertySelections) {
    persistPropertyPickerRecents(state.recentPropertySelections);
  }
  if (state.userPropertyGroups !== prev.userPropertyGroups) {
    persistPropertyPickerGroups(state.userPropertyGroups);
  }
  if (state.favoriteProperties !== prev.favoriteProperties) {
    persistFavoriteProperties(state.favoriteProperties);
  }
  if (state.favoriteTenantIds !== prev.favoriteTenantIds) {
    persistFavoriteTenants(state.favoriteTenantIds);
  }
  if (state.invoiceItemsViewMode !== prev.invoiceItemsViewMode) {
    persistInvoiceItemsViewMode(state.invoiceItemsViewMode);
  }
});

export function resetAppClientStore(): void {
  useAppClientStore.setState(initialAppClientState());
}

export function useGreetingState(): [
  string,
  (updater: SetStateAction<string>) => void,
] {
  const greeting = useAppClientStore((s) => s.greeting);
  const setGreeting = useCallback((updater: SetStateAction<string>) => {
    useAppClientStore.setState((state) => ({
      greeting: resolveUpdater(state.greeting, updater),
    }));
  }, []);
  return [greeting, setGreeting];
}

export function usePageBannerState(): [
  boolean,
  (updater: SetStateAction<boolean>) => void,
] {
  const pageBanner = useAppClientStore((s) => s.pageBanner);
  const setPageBanner = useCallback((updater: SetStateAction<boolean>) => {
    useAppClientStore.setState((state) => ({
      pageBanner: resolveUpdater(state.pageBanner, updater),
    }));
  }, []);
  return [pageBanner, setPageBanner];
}

export function usePageBannerValue(): boolean {
  return useAppClientStore((s) => s.pageBanner);
}

export function useRecentPropertySelections(): [
  RecentsStorageV2,
  (updater: SetStateAction<RecentsStorageV2>) => void,
] {
  const recentPropertySelections = useAppClientStore(
    (s) => s.recentPropertySelections
  );
  const setRecentPropertySelections = useCallback(
    (updater: SetStateAction<RecentsStorageV2>) => {
      useAppClientStore.setState((state) => ({
        recentPropertySelections: resolveUpdater(
          state.recentPropertySelections,
          updater
        ),
      }));
    },
    []
  );
  return [recentPropertySelections, setRecentPropertySelections];
}

export function useUserPropertyGroups(): [
  SavedGroupsStorageV2,
  (updater: SetStateAction<SavedGroupsStorageV2>) => void,
] {
  const userPropertyGroups = useAppClientStore((s) => s.userPropertyGroups);
  const setUserPropertyGroups = useCallback(
    (updater: SetStateAction<SavedGroupsStorageV2>) => {
      useAppClientStore.setState((state) => ({
        userPropertyGroups: resolveUpdater(state.userPropertyGroups, updater),
      }));
    },
    []
  );
  return [userPropertyGroups, setUserPropertyGroups];
}

export function useFavoriteProperties(): [
  string[],
  (updater: SetStateAction<string[]>) => void,
] {
  const favoriteProperties = useAppClientStore((s) => s.favoriteProperties);
  const setFavoriteProperties = useCallback(
    (updater: SetStateAction<string[]>) => {
      useAppClientStore.setState((state) => ({
        favoriteProperties: resolveUpdater(state.favoriteProperties, updater),
      }));
    },
    []
  );
  return [favoriteProperties, setFavoriteProperties];
}

/**
 * Tenant ids starred in the sidebar switcher. Persisted in localStorage on
 * this browser, independent of the server session.
 */
export function useFavoriteTenants(): [
  string[],
  (updater: SetStateAction<string[]>) => void,
] {
  const favoriteTenantIds = useAppClientStore((s) => s.favoriteTenantIds);
  const setFavoriteTenantIds = useCallback(
    (updater: SetStateAction<string[]>) => {
      useAppClientStore.setState((state) => ({
        favoriteTenantIds: resolveUpdater(state.favoriteTenantIds, updater),
      }));
    },
    []
  );
  return [favoriteTenantIds, setFavoriteTenantIds];
}

/**
 * Persisted user preference for how invoice line items are rendered when
 * drilling into an invoice. One source of truth across the invoices grid
 * row-expand sub-row, the Create-Drafts preview sub-row, and the invoice
 * detail page's "Line items" section.
 */
export function useInvoiceItemsViewMode(): [
  InvoiceItemsViewMode,
  (updater: SetStateAction<InvoiceItemsViewMode>) => void,
] {
  const mode = useAppClientStore((s) => s.invoiceItemsViewMode);
  const setMode = useCallback(
    (updater: SetStateAction<InvoiceItemsViewMode>) => {
      useAppClientStore.setState((state) => ({
        invoiceItemsViewMode: resolveUpdater(
          state.invoiceItemsViewMode,
          updater
        ),
      }));
    },
    []
  );
  return [mode, setMode];
}
